import React, {ChangeEvent, useState} from 'react';
import {Duration, DurationObjectUnits} from "luxon";

// Helpers ----

export enum DurationUnit {
  SECOND = 'second',
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

function extractDurationValues(duration: Duration, enabledUnits: Array<DurationUnit>): DurationObjectUnits {
  const values = duration.shiftTo(...enabledUnits).toObject()

  return Object.fromEntries(
    Object
      .entries(values)
      .map(([unit, value]) => [unit, Math.floor(value)])
  )
}

// Component ----

type DurationPickerProps = {
  id: string
  label: string
  hint?: string
  units?: Array<DurationUnit>
  value?: Duration | null
  onChange: (duration: Duration) => void
}

function DurationPicker({id, hint, label, onChange, units, value}: DurationPickerProps) {

  // Determine which units to display, ordered from largest to smallest
  const enabledUnits = Object
    .values(DurationUnit)
    .filter(value => !units || units.includes(value))
    .reverse()

  // Keep track of the unit values
  const [values, setValues] = useState<DurationObjectUnits>(value? extractDurationValues(value, enabledUnits) : {});

  function handleUnitValueChange(unit: DurationUnit, event: ChangeEvent<HTMLInputElement>) {
    const value = event.currentTarget.value
    const cleanValue = (value === '' ? undefined : value)

    const newValues = {...values, [`${unit}s`]: cleanValue}

    setValues(newValues)

    if (onChange) {
      onChange(Duration.fromObject(newValues))
    }
  }

  // Render control
  return (
    <div>
      <div className="flex justify-between">
        <label className="block text-sm font-medium leading-6 text-gray-900">{label}</label>

        {!!hint &&
          <span className="text-sm leading-6 text-gray-500">{hint}</span>
        }
      </div>

      <fieldset className="mt-2">
        <legend className="sr-only">{label}</legend>

        <div className='space-y-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-4'>
          {enabledUnits.map(unit => (
            <div className="relative rounded-md shadow-sm w-32" key={unit}>
              <input
                type="number"
                name={unit}
                id={`${id}-${unit}-input`}
                className="block w-full rounded-md border-0 py-1.5 pl-7 pr-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand sm:text-sm sm:leading-6"
                step={1}
                min={0}
                aria-describedby={`${id}-${unit}`}
                value={values[`${unit}s`] ?? ''}
                onChange={event => handleUnitValueChange(unit, event)}
              />

              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-gray-500 sm:text-sm" id={`${id}-${unit}`}>
                    {`${unit}s`}
                  </span>
              </div>
            </div>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

export default DurationPicker;